import client from "../client";

export const updateOrganisationForXero = (code: string) => {
  return client({
    query: `
      mutation GetUserOrganisations($code: String!) {
        update_organisation_token_for_xero(code: $code){
          message
        }
      }`,
    variables: {
      code
    }
  });
};

interface UpdateXeroTokenForOrganisationRequest {
  access_token: string;
  refresh_token: string;
  id_token: string;
  scope: string;
  token_type: string;
}

export const updateXeroTokenForOrganisation = (data: UpdateXeroTokenForOrganisationRequest) => {
  return client({
    query: `
      mutation UpdateXeroTokenForOrganisation($input: UpdateXeroTokenForOrgInput) {
        update_xero_token_for_organisation(input: $input){
          message
        }
      }`,
    variables: {
      input: data
    }
  });
};

export const getOrganisationXeroAccounts = () => {
  return client({
    query: `
      query GetOrganisationXeroAccounts {
        get_organisation_xero_accounts {
          accountId
          code
          name
          description
          taxType
          type
        }
      }`,
    variables: {}
  });
};

export const getXeroTrackingCategories = () => {
  return client({
    query: `
      query GetXeroTrackingCategories {
        get_organisation_xero_categories {
          trackingCategoryID
          name
          options {
            trackingOptionID
            name
          }
        }
      }`,
    variables: {}
  });
};

export interface CreateBankAccountRequest {
  name?: string;
  description?: string;
  bank_account_number?: string;
  bank_account_accountId?: string;
}

export const createBankAccount = (data: CreateBankAccountRequest) => {
  return client({
    query: `
      mutation CreateXeroBankAccount ($input: CreateXeroBankAccountInput) {
        create_xero_bank_acccount (input: $input) {
          code
          message
        }
      }`,
    variables: {
      input: data
    }
  });
};

export interface SyncOrganisationTransactionsWithXeroRequest {
  start_date: string;
  end_date: string;
  organisation_id: string;
  activity_ids: string[];
}

export const syncOrganisationTransactionsWithXero = (data: SyncOrganisationTransactionsWithXeroRequest) => {
  return client({
    query: `
      mutation SyncOrganisationTransactionsWithXero ($input: SyncOrganisationTransactionsWithXeroInput) {
        sync_organisation_transactions_with_xero (input: $input) {
          code
          message
        }
      }`,
    variables: {
      input: data
    }
  });
};

export type AccountingSyncStatus =
  | "SUCCEEDED"
  | "FAILED"
  | "INELIGIBLE"
  | "WAITING_FOR_RECONNECT"
  | "BLOCKED_DISCONNECTED"
  | "NO_STRATEGY";

export interface AccountingSyncOutcome {
  provider: string;
  status: AccountingSyncStatus;
  job_id: string;
  external_id?: string | null;
  error?: { class: string; message: string } | null;
  eligibility_rule?: string | null;
  eligibility_reason?: string | null;
}

export interface AccountingSyncResponse {
  activity_id: string;
  activity_version: string;
  outcomes: AccountingSyncOutcome[];
}

export const syncActivity = (activity_id: string) => {
  return client({
    query: `
      mutation SyncActivity ($input: SyncActivityInput!) {
        sync_activity (input: $input) {
          activity_id
          activity_version
          outcomes {
            provider
            status
            job_id
            external_id
            error { class message }
            eligibility_rule
            eligibility_reason
          }
        }
      }`,
    variables: { input: { activity_id } }
  });
};

export interface ActivityProviderRecord {
  provider: string;
  external_id: string;
  status: "ACTIVE" | "SUPERSEDED";
  external_state: "CONFIRMED_ALIVE" | "CONFIRMED_DELETED" | "UNKNOWN";
  synced_at?: string | null;
  superseded_at?: string | null;
  superseded_reason?: string | null;
  provider_status_at_supersession?: string | null;
}

export interface AccountingActivitySyncState {
  activity_id: string;
  records: ActivityProviderRecord[];
}

export const getActivityAccountingIntegrationRecords = (activity_id: string) => {
  return client({
    query: `
      query GetActivityAccountingIntegrationRecords ($input: GetActivityAccountingIntegrationRecordsInput!) {
        get_activity_accounting_integration_records (input: $input) {
          activity_id
          records {
            provider
            external_id
            status
            external_state
            synced_at
            superseded_at
            superseded_reason
            provider_status_at_supersession
          }
        }
      }`,
    variables: { input: { activity_id } }
  });
};

export const createXeroTokenForOrganisation = (url: string) => {
  return client({
    query: `
      mutation CreateXeroTokenForOrganisation($url: String!) {
        create_xero_token_for_organisation(url: $url) {
        message
        }
      } 
    `,
    variables: {
      url
    }
  });
};

export const disconnectXero = () => {
  return client({
    query: `
      mutation DisconnectXero {
        disconnect_xero {
          organisation_id
          provider
          status
          is_authenticated
          tenant {
            id
            name
          }
          last_refreshed_at
          last_used_at
          updated_at
          disconnect {
            by
            reason
          }
          config {
            transactions_to_sync
            default_bank_account {
              id
              name
              code
              description
            }
          }
        }
      }
    `,
    variables: {}
  });
};

export interface AccountingBankAccount {
  id: string;
  name: string;
  code?: string | null;
  description?: string | null;
}

export interface XeroTrackingCategoryMappingEntry {
  xero_tracking_category_id: string;
  xero_tracking_option_id?: string;
}

export interface TransactionTypeDefaults {
  bank_account?: AccountingBankAccount;
  xero_account_id?: string;
  xero_tax_type?: string;
  xero_tracking_category_mapping?: XeroTrackingCategoryMappingEntry[];
}

export type DefaultsByTransactionType = Record<string, TransactionTypeDefaults>;

export interface AccountingConnectionConfig {
  transactions_to_sync?: string[] | null;
  default_bank_account?: AccountingBankAccount | null;
  sync_assets_enabled?: boolean | null;
  defaults_by_transaction_type?: DefaultsByTransactionType | null;
}

export interface AccountingConnection {
  organisation_id: string;
  provider: string;
  status: "ACTIVE" | "NEEDS_RECONNECT" | "DISCONNECTED" | string;
  is_authenticated?: boolean;
  tenant?: {
    id?: string;
    name?: string;
  };
  last_refreshed_at?: string | null;
  last_used_at?: string | null;
  updated_at?: string | null;
  disconnect?: {
    by?: string | null;
    reason?: string | null;
  } | null;
  config?: AccountingConnectionConfig | null;
}

export const getAccountingConnections = () => {
  return client({
    query: `
      query GetAccountingConnections {
        get_accounting_connections {
          organisation_id
          provider
          status
          is_authenticated
          tenant {
            id
            name
          }
          last_refreshed_at
          last_used_at
          updated_at
          disconnect {
            by
            reason
          }
          config {
            transactions_to_sync
            default_bank_account {
              id
              name
              code
              description
            }
            sync_assets_enabled
            defaults_by_transaction_type
          }
        }
      }
    `,
    variables: {}
  });
};

export interface SaveXeroConfigRequest {
  transactions_to_sync: string[];
  sync_assets_enabled?: boolean;
  defaults_by_transaction_type?: DefaultsByTransactionType;
}

export const saveXeroConfig = (data: SaveXeroConfigRequest) => {
  const { defaults_by_transaction_type, ...rest } = data;
  const input: Record<string, unknown> = { ...rest };

  if (defaults_by_transaction_type !== undefined) {
    input.defaults_by_transaction_type = JSON.stringify(defaults_by_transaction_type);
  }

  return client({
    query: `
      mutation SaveXeroConfig($input: SaveXeroConfigInput!) {
        save_xero_config(input: $input) {
          organisation_id
          provider
          status
          is_authenticated
          tenant {
            id
            name
          }
          last_refreshed_at
          last_used_at
          disconnect {
            by
            reason
          }
          config {
            transactions_to_sync
            default_bank_account {
              id
              name
              code
              description
            }
            sync_assets_enabled
            defaults_by_transaction_type
          }
        }
      }
    `,
    variables: { input }
  });
};

export const connectXero = (callback_url: string) => {
  return client({
    query: `
      mutation ConnectXero($callback_url: String!) {
        connect_xero(callback_url: $callback_url) {
          organisation_id
          provider
          status
          is_authenticated
          tenant {
            id
            name
          }
          last_refreshed_at
          last_used_at
          updated_at
          disconnect {
            by
            reason
          }
          config {
            transactions_to_sync
            default_bank_account {
              id
              name
              code
              description
            }
          }
        }
      }
    `,
    variables: { callback_url }
  });
};

export const disconnectXeroV2 = () => {
  return client({
    query: `
      mutation DisconnectXeroV2 {
        disconnect_xero {
          organisation_id
          provider
          status
          is_authenticated
          tenant {
            id
            name
          }
          last_refreshed_at
          last_used_at
          updated_at
          disconnect {
            by
            reason
          }
          config {
            transactions_to_sync
            default_bank_account {
              id
              name
              code
              description
            }
          }
        }
      }
    `,
    variables: {}
  });
};

export interface TestXeroConnectionResult {
  status: string;
  last_tested_at?: string;
}

export const testXeroConnection = () => {
  return client({
    query: `
      query TestXeroConnection {
        test_xero_connection {
          status
          last_tested_at
        }
      }
    `,
    variables: {}
  });
};

export const getOrganisationXeroTaxRate = () => {
  return client({
    query: `
      query GetOrganisationXeroTaxRate {
        get_organisation_xero_tax_rate {
          taxType
          name
        }
      }
    `,
    variables: {}
  });
};
